/**
 * PlayerLayout — top bar shell shared by Menu / Browse / Search.
 * Branding + clock + notifications bell + playlist switcher + settings menu.
 */
import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDevice } from "../lib/DeviceContext";
import useHardwareBackButton from "../lib/useHardwareBackButton";
import {
  getPlayerBranding,
  getAssetUrl,
  getUnreadNotificationsCount,
  getAssignedPlaylists,
} from "../lib/playerApi";
import { PlaylistDataProvider, usePlaylistData } from "../lib/PlaylistDataContext";
import { Bell, Settings as SettingsIcon, ListVideo, RefreshCcw, Users, Loader2 } from "lucide-react";
import { PLAYER } from "../lib/testIds";
import { getActiveProfile, AVATAR_COLORS } from "../lib/profiles";
import PlaylistUpdatingSplash from "./PlaylistUpdatingSplash";
import MobileBottomNav from "./MobileBottomNav";
import { scheduleProfileCacheSync } from "../lib/profileCacheSync";
import "../vu-theme.css";

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function PlayerLayout() {
  useHardwareBackButton();
  const { panelName, isActivated } = useDevice();
  const navigate = useNavigate();
  const location = useLocation();
  const now = useClock();
  const [branding, setBranding] = useState(null);
  const [unread, setUnread] = useState(0);
  const [playlists, setPlaylists] = useState([]);
  const [activePlaylistId, setActivePlaylistId] = useState(
    localStorage.getItem("vupp_active_playlist") || null
  );

  // v1.4.13 — Windows Web Player kiosk back/exit UX bridge.
  //   1. Report at-root state whenever the SPA route changes so main
  //      knows when a second Esc / Backspace should raise the exit
  //      confirmation dialog.
  //   2. Subscribe to `nav:go-back` from main — when the user hits
  //      Esc / Backspace and mpv isn't playing, we `navigate(-1)` if
  //      the history stack has anything to pop, else main will treat
  //      the next press as an exit-confirm trigger (because
  //      `reportAtRoot(true)` will have been sent).
  useEffect(() => {
    const api = typeof window !== "undefined" ? window.electronAPI : null;
    if (!api || !api.isKioskWebPlayer) return undefined;
    const ROOT_ROUTES = new Set(["/player/browse", "/player", "/player/"]);
    const atRoot = ROOT_ROUTES.has(location.pathname);
    try { api.reportAtRoot(atRoot); } catch { /* ignore */ }
    try { api.reportDidNavigate(); } catch { /* ignore */ }
    const unsub = api.onGoBackRequest?.(() => {
      // If we're at the root there's nothing to pop — tell main we
      // stayed put so the next Esc raises the exit-confirm dialog.
      if (atRoot) {
        try { api.reportAtRoot(true); } catch { /* ignore */ }
        return;
      }
      navigate(-1);
    });
    return () => { try { unsub && unsub(); } catch { /* ignore */ } };
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!isActivated) return;
    // v1.3.36 — opportunistic profile-cache sync once per layout mount
    // so the server gets the freshest snapshot even when the user only
    // tweaked something the favorites/history hooks don't cover (e.g.
    // an app-settings toggle).
    scheduleProfileCacheSync();
    let cancelled = false;
    (async () => {
      try {
        const b = await getPlayerBranding();
        if (!cancelled) setBranding(b);
      } catch {
        /* ignore */
      }
      try {
        const c = await getUnreadNotificationsCount();
        if (!cancelled) setUnread(c.unread_count || 0);
      } catch {
        /* ignore */
      }
      try {
        const r = await getAssignedPlaylists();
        if (cancelled) return;
        setPlaylists(r.playlists || []);
        if (!activePlaylistId && r.playlists?.length > 0) {
          setActivePlaylistId(r.playlists[0].id);
          localStorage.setItem("vupp_active_playlist", r.playlists[0].id);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivated]);

  const refreshNotifications = async () => {
    try {
      const c = await getUnreadNotificationsCount();
      setUnread(c.unread_count || 0);
    } catch {
      /* ignore */
    }
  };

  const activePlaylist = playlists.find((p) => p.id === activePlaylistId);

  const logoUrl = branding ? getAssetUrl(branding.logo_url) : null;
  // v1.3.34 — strip the " ADMIN" suffix the backend branding sends.
  const appName = ((branding?.app_name || panelName || "ZA Player Pro") + "")
    .replace(/\s*ADMIN\s*$/i, "");

  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const activeProfile = getActiveProfile();
  const profileColor = activeProfile
    ? AVATAR_COLORS[activeProfile.avatar_index ?? 0] || AVATAR_COLORS[0]
    : null;
  const initials = activeProfile
    ? activeProfile.name
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  return (
    <PlaylistDataProvider playlist={activePlaylist}>
    <div className="vu-theme min-h-screen bg-[#07080b] text-slate-100">
      <header
        data-testid={PLAYER.topbar}
        className="sticky top-0 z-30 bg-[#0b0d12]/85 backdrop-blur border-b border-white/5"
      >
        {/* Width caps + TV-safe padding:
             • Phones    → 12 px gutters, single-line top bar
             • Tablets   → 24 px gutters
             • Desktop   → cap at 1600px so things stay readable
             • TVs ≥4K   → cap at 90vw and use 5 % TV-safe inset, otherwise
               on a 3840-wide TV the UI floats in a 1600px island in the
               middle of the screen. */}
        <div className="mx-auto max-w-[1600px] 2xl:max-w-[90vw] flex items-center gap-3 sm:gap-5 px-3 sm:px-6 2xl:px-[5vw] py-3">
          {/* vupro-style: NO logo/wordmark on the left — keeps the home
              hero card the visual anchor. We expose a tiny click-target
              on the brand area so users can still tap "home". */}
          <Link
            to="/player/menu"
            className="flex items-center gap-2.5 group opacity-80 hover:opacity-100 transition-opacity"
            data-testid="player-home-link"
            title="Home"
          >
            <img
              src={logoUrl || "/za-logo-icon.png"}
              alt="ZA Player Pro"
              // v1.0.9 — Fallback chain so a broken/stale branding URL
              // never leaves us showing the broken-image glyph. First
              // try the local bundled icon, then hide the element
              // entirely so the wordmark alone represents the brand.
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallbackTried === "1") {
                  img.style.display = "none";
                  return;
                }
                img.dataset.fallbackTried = "1";
                img.src = "/za-logo-icon.png";
              }}
              className="w-9 h-9 object-contain flex-shrink-0"
            />
            <span
              className="text-sm font-semibold tracking-wide text-slate-100 hidden sm:inline"
              data-testid="player-brand-wordmark"
            >
              ZA Player <span className="text-orange-400">Pro</span>
            </span>
          </Link>

          <PlaylistReloadPill />

          <div className="flex-1" />

          <PlaylistReloadButton />

          <button
            data-testid={PLAYER.btnNotifications}
            onClick={() => {
              navigate("/player/notifications");
              setTimeout(refreshNotifications, 500);
            }}
            className="relative p-2 rounded-md hover:bg-white/5 text-slate-300 hover:text-white transition-colors"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span
                data-testid={PLAYER.notificationsBadge}
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-[10px] font-bold text-white flex items-center justify-center"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>

          <button
            data-testid={PLAYER.btnSettings}
            onClick={() => navigate("/player/settings")}
            className="p-2 rounded-md hover:bg-white/5 text-slate-300 hover:text-white transition-colors"
            title="Settings"
          >
            <SettingsIcon className="w-5 h-5" />
          </button>

          {/* Profile pill — click to switch */}
          {activeProfile && (
            <button
              data-testid="player-profile-pill"
              onClick={() => navigate("/player/profiles")}
              className={`flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 border transition-colors ${
                activeProfile.is_kids ? "border-cyan-400/60" : "border-white/10"
              }`}
              title="Switch profile"
            >
              <span
                className={`w-7 h-7 rounded-full bg-gradient-to-br ${profileColor.from} ${profileColor.to} flex items-center justify-center text-xs font-bold text-white ${
                  activeProfile.is_kids ? "ring-2 ring-cyan-400" : ""
                }`}
              >
                {initials}
              </span>
              <span className="text-sm font-medium hidden sm:inline max-w-[120px] truncate">
                {activeProfile.name}
              </span>
              {activeProfile.is_kids && (
                <span className="hidden sm:inline text-[10px] font-bold tracking-wide bg-cyan-400 text-black rounded-full px-1.5 py-0.5">
                  KIDS
                </span>
              )}
              <Users className="w-3 h-3 text-slate-400 hidden sm:inline" />
            </button>
          )}

          {/* Clock — rightmost, vupro-style */}
          <span className="text-sm font-mono text-white/80 hidden sm:block tabular-nums">
            {timeStr}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] 2xl:max-w-[90vw] px-3 sm:px-6 2xl:px-[5vw] py-4 sm:py-6">
        <Outlet
            context={{
              activePlaylistId,
              setActivePlaylistId: (id) => {
                setActivePlaylistId(id);
                localStorage.setItem("vupp_active_playlist", id);
              },
              playlists,
              activePlaylist,
              refreshPlaylists: async () => {
                const r = await getAssignedPlaylists();
                setPlaylists(r.playlists || []);
              },
            }}
          />
      </main>

      {/* Funny playlist-updating splash — only shows on the FIRST load when
          we have nothing cached yet. Self-dismisses when channels arrive. */}
      <PlaylistUpdatingSplash />

      {/* v1.3.58 — Non-blocking "Updating playlist…" pill, used when the
          admin has flipped on `snapshot_required_on_start` for the active
          playlist. The user can keep browsing cached data; the pill just
          tells them a refresh is rolling in the background. Hidden when
          the big splash is up (i.e. cold-start with nothing cached). */}
      <UpdatingPlaylistPill />

      {/* Mobile-only purple bottom-nav (Live / Movies / Series / Settings).
          Hidden ≥ md breakpoint so tablets + TVs keep the topbar-only layout. */}
      <MobileBottomNav />
    </div>
    </PlaylistDataProvider>
  );
}

/**
 * Small pill that surfaces playlist load status + lets the user trigger a
 * manual re-fetch. Rendered inside the PlayerLayout header.
 */
function PlaylistReloadPill() {
  const { status, source, fetched_at, channels, reload, playlist } = usePlaylistData();
  if (!playlist) return null;
  const loading = status === "loading";
  const errored = status === "error";
  const age = fetched_at ? Math.round((Date.now() - fetched_at) / 60000) : null;
  return (
    <button
      data-testid="player-playlist-reload"
      onClick={() => !loading && reload()}
      disabled={loading}
      title={
        errored
          ? "Failed to load — click to retry"
          : `${channels.length.toLocaleString()} items · ${
              source === "cache" ? `cached ${age}m ago` : "freshly loaded"
            } · click to reload`
      }
      className={`hidden md:flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] border transition-colors ${
        errored
          ? "bg-red-500/10 text-red-300 border-red-500/30 hover:bg-red-500/20"
          : loading
          ? "bg-white/5 text-slate-400 border-white/10"
          : "bg-white/5 text-slate-300 hover:bg-white/10 border-white/10"
      }`}
    >
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <RefreshCcw className="w-3 h-3" />
      )}
      <span className="tabular-nums">
        {loading
          ? "Loading…"
          : errored
          ? "Reload"
          : channels.length.toLocaleString()}
      </span>
    </button>
  );
}



// Top-bar refresh icon — used inside the `<PlaylistDataProvider>` so it
// can call the playlist `reload()` instead of `window.location.reload()`.
// The latter merely re-mounts the WebView and re-reads the SAME cached
// playlist, making the button look broken: clicking it did nothing
// visible to the user (especially when their goal is "get the new
// channels my reseller just added"). This version drops IDB for the
// active playlist + re-runs the catalog import.
function PlaylistReloadButton() {
  const { reload, status } = usePlaylistData();
  const loading = status === "loading";
  return (
    <button
      data-testid={PLAYER.btnReloadPlaylist}
      onClick={() => !loading && reload()}
      className="p-2 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
      disabled={loading}
      title={loading ? "Reloading playlist…" : "Reload playlist from server"}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <RefreshCcw className="w-4 h-4" />
      )}
    </button>
  );
}

// v1.3.58 — Non-blocking "Updating playlist…" pill. Renders fixed in the
// top-center (just under the header) whenever:
//   • the active playlist has `snapshot_required_on_start === true`, AND
//   • the playlist is currently refreshing in the background, AND
//   • we already have at least some cached data on screen
//     (so the fullscreen `PlaylistUpdatingSplash` isn't already up).
// This is the lightweight pill the admin opts into per playlist via the
// toggle on the Cover & Snapshot admin page.
function UpdatingPlaylistPill() {
  const { playlist, status, channels } = usePlaylistData();
  if (!playlist?.snapshot_required_on_start) return null;
  if (status !== "loading") return null;
  if ((channels?.length ?? 0) === 0) return null; // big splash handles this case
  return (
    <div
      data-testid="player-updating-playlist-pill"
      role="status"
      aria-live="polite"
      className="fixed top-[68px] left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 text-xs shadow-lg backdrop-blur-md pointer-events-none animate-pulse"
    >
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      <span>Updating playlist…</span>
    </div>
  );
}
